// frontend/src/components/Artists/ArtistArtworkList.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import ArtworkCard from "../CategoryPage/ArtworkCard";
import LoadingSpinner from "../common/LoadingSpinner";
import "./ArtistArtworkList.css";

const ArtistArtworkList = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();

  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const observerRef = useRef();
  const lastArtworkRef = useRef();

  // Load artist details
  useEffect(() => {
    const loadArtist = async () => {
      try {
        const data = await api.getArtistById(artistId);
        setArtist(data);
      } catch (err) {
        setError("Artist not found");
        console.error(err);
      }
    };
    loadArtist();
  }, [artistId]);

  // Reset when artist changes
  useEffect(() => {
    setArtworks([]);
    setPage(1);
    setHasMore(true);
    setInitialLoading(true);
  }, [artistId]);

  // Load artworks when page changes
  useEffect(() => {
    const loadArtworks = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getArtworksByArtist(artistId, page);
        setArtworks((prev) => [...prev, ...data.artworks]);
        setHasMore(data.hasMore);
      } catch (err) {
        setError("Failed to load artworks");
        console.error(err);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };
    loadArtworks();
  }, [artistId, page]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading || !hasMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((p) => p + 1);
      }
    });

    observerRef.current = observer;
    if (lastArtworkRef.current) {
      observer.observe(lastArtworkRef.current);
    }

    return () => observer.disconnect();
  }, [loading, hasMore]);

  const handleSelectArtwork = (artwork) => {
    const limit = 12;
    navigate(`/artwork/${artwork._id}`, {
      state: {
        artworkList: artworks,
        artistId,
        artistName: artist?.fullName || `${artist?.fname} ${artist?.lname}`,
        hasMore,
        currentPage: page,
        pageLimit: limit,
      },
    });
  };

  if (initialLoading) {
    return <LoadingSpinner message="Loading artworks..." />;
  }

  return (
    <div className="artist-artwork-main">
      <div className="artist-artwork-content">
        {/* Artist Header */}
        <div className="artist-header">
          <div className="artist-header-image-container">
            <img
              src={artist?.imageUrl || "/images/default-artist.jpg"}
              alt={artist?.fullName || `${artist?.fname} ${artist?.lname}`}
              className="artist-header-image"
            />
          </div>
          <div className="artist-header-info">
            <h1 className="artist-header-title">
              {artist?.fullName || `${artist?.fname} ${artist?.lname}`}
            </h1>
            {/* {artist?.birthday && (
              <p className="artist-header-detail">
                Born: {new Date(artist.birthday).toLocaleDateString()}
              </p>
            )} */}
            {artist?.startdate && (
              <p className="artist-header-detail">
                Started painting in {new Date(artist.startdate).getFullYear()}
              </p>
            )}
          </div>
        </div>

        {error && <div className="artist-artwork-error">{error}</div>}

        <div className="artworks-grid">
          {artworks.map((artwork, idx) => (
            <ArtworkCard
              key={artwork._id}
              artwork={artwork}
              onClick={() => handleSelectArtwork(artwork)}
              ref={idx === artworks.length - 1 ? lastArtworkRef : null}
            />
          ))}
        </div>

        {loading && (
          <div className="artist-artwork-loading">
            <LoadingSpinner />
          </div>
        )}

        {!loading && artworks.length === 0 && (
          <div className="artist-artwork-empty">
            No artworks found for this artist.
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistArtworkList;
